import { Link } from "react-router-dom";
import { ChevronRight, MapPin, Sparkles, ScrollText, ShoppingBag, Globe, Package } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import AdBanner from "@/components/AdBanner";
import SendYourStory from "@/components/SendYourStory";
import AdventurerConcept from "@/components/AdventurerConcept";
import StorePreview from "@/components/StorePreview";
import NavBar from "@/components/NavBar";
import Leaderboard from "@/components/Leaderboard";
import TrendingToday from "@/components/TrendingToday";

export default function Landing() {
  const { t, lang, setLang } = useLang();

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-white text-zinc-900">
      <NavBar />
      <section className="relative min-h-[calc(100vh-64px)] flex flex-col">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-stone-50 via-white to-stone-100" />
        <div className="absolute inset-x-0 top-0 h-[60vh] -z-10 opacity-40"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, rgba(139,111,61,0.12), transparent 50%), radial-gradient(circle at 80% 40%, rgba(120,53,15,0.08), transparent 55%)"
             }}
        />
        <div className="grain absolute inset-0 -z-0 pointer-events-none"></div>

        {/* Main hero */}
        <main className="relative z-10 flex-1 flex flex-col justify-center max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="mb-6 flex items-center gap-3 text-[#8b6f3d]">
            <div className="h-px w-12 bg-[#8b6f3d]/40"></div>
            <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{t("atlasTag")}</span>
          </div>
          <h1
            className="font-serif-mystery text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] mb-6 max-w-5xl text-zinc-900"
            data-testid="landing-title"
          >
            {lang === "fr" ? (
              <>La seule carte<br /><span className="italic text-[#8b6f3d]">dont vous aurez besoin</span></>
            ) : (
              <>The Only Map<br /><span className="italic text-[#8b6f3d]">You Will Ever Need</span></>
            )}
          </h1>
          <p className="font-mono-data text-sm sm:text-base tracking-widest uppercase text-zinc-500 mb-12"
             data-testid="landing-subtitle">
            {t("subtitle")}
          </p>

          <div className="flex flex-wrap gap-4 items-center mb-16">
            <Link to="/map">
              <Button
                size="lg"
                data-testid="explore-map-btn"
                className="group h-14 px-8 bg-[#8b6f3d] hover:bg-[#a1854b] text-white font-medium text-base rounded-full transition-colors"
              >
                {t("exploreBtn")}
                <ChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
            </Link>
            <Link to="/store">
              <Button
                size="lg"
                variant="outline"
                data-testid="landing-store-btn"
                className="group h-14 px-6 border-[#8b6f3d]/50 hover:bg-amber-50 text-[#8b6f3d] font-medium text-base rounded-full transition-colors"
              >
                <ShoppingBag className="mr-2" size={16} />
                {lang === "fr" ? "Boutique" : "Store"}
              </Button>
            </Link>
            <div className="font-mono-data text-xs text-zinc-500 flex items-center gap-2">
              <Sparkles size={14} className="text-[#be185d]" />
              250+ {t("placesCount")} · {t("aiPowered")}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl">
            <p className="text-zinc-700 leading-relaxed border-l border-[#8b6f3d]/30 pl-4">
              {t("presentation")}
            </p>
            <p className="text-zinc-700 leading-relaxed border-l border-[#8b6f3d]/30 pl-4">
              {t("presentation2")}
            </p>
            <p className="text-zinc-700 leading-relaxed border-l border-[#8b6f3d]/30 pl-4">
              {t("presentation3")}
            </p>
          </div>
        </main>
      </section>

      {/* Landing banner ad */}
      <section className="relative border-t border-zinc-100 px-6 md:px-12 max-w-6xl mx-auto w-full">
        <AdBanner testId="landing-top-ad" />
      </section>

      {/* Top 10 Adventurers + Trending today (new sections at top) */}
      <Leaderboard />
      <TrendingToday />

      {/* Weekly update banner */}
      <section className="relative border-t border-zinc-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 px-6 md:px-12 py-10" data-testid="landing-updates-banner">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#8b6f3d]/15 text-[#8b6f3d] flex items-center justify-center shrink-0">
              <Globe size={22} />
            </div>
            <div>
              <h3 className="font-serif-mystery text-xl text-zinc-900 mb-1">
                {lang === "fr" ? "🌍 Nouveaux mystères chaque semaine" : "🌍 New mysteries every week"}
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                {lang === "fr"
                  ? "De nouvelles histoires, de nouveaux lieux et de nouveaux témoignages sont ajoutés chaque semaine à la carte. Revenez régulièrement pour explorer."
                  : "New stories, new places and new testimonies are added to the map every week. Come back often to explore."}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#8b6f3d]/15 text-[#8b6f3d] flex items-center justify-center shrink-0">
              <Package size={22} />
            </div>
            <div>
              <h3 className="font-serif-mystery text-xl text-zinc-900 mb-1">
                {lang === "fr" ? "🛍️ Nouveaux produits en boutique" : "🛍️ New products in the store"}
              </h3>
              <p className="text-sm text-zinc-600 leading-relaxed">
                {lang === "fr"
                  ? "De nouveaux t-shirts et objets d'exploration paranormale sont ajoutés régulièrement. Portez le mystère avec vous."
                  : "New T-shirts and paranormal exploration items are added regularly. Wear the mystery with you."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Adventurer concept — become a mystery explorer */}
      <AdventurerConcept />

      {/* Store preview — 5 exclusive t-shirts */}
      <StorePreview />

      {/* Send your story + regular updates */}
      <SendYourStory />

      {/* Disclaimer */}
      <section className="relative border-t border-zinc-200 px-6 md:px-12 py-16 max-w-4xl mx-auto">
        <div className="flex items-start gap-4">
          <ScrollText className="text-[#8b6f3d] shrink-0 mt-1" size={20} strokeWidth={1.4} />
          <div>
            <h3 className="font-serif-mystery text-2xl mb-3 text-[#8b6f3d]">{t("disclaimerTitle")}</h3>
            <p className="text-zinc-600 leading-relaxed" data-testid="disclaimer-text">{t("disclaimer")}</p>
          </div>
        </div>
      </section>

      {/* Footer bottom banner ad + Footer */}
      <section className="relative border-t border-zinc-100 px-6 md:px-12 max-w-6xl mx-auto w-full">
        <AdBanner testId="landing-bottom-ad" />
      </section>
      <Footer />
    </div>
  );
}
